import { TextField } from "@openanalyst-ai/ui/text-field"
import { Splash } from "@openanalyst-ai/ui/logo"
import { Button } from "@openanalyst-ai/ui/button"
import { Component, createSignal, onCleanup, onMount, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { isMissingOpenAnalystSessionError } from "@/context/auth/broker"
import { isNetworkError } from "@/utils/network-error"
import { usePlatform, type DiagnosticLogEntry } from "@/context/platform"
import { useLanguage } from "@/context/language"
import {
  buildErrorReportPayload,
  defaultErrorReportUrl,
  formatDiagnosticEntryForReport,
  submitErrorReport,
} from "./error-report"

export type InitError = {
  name: string
  data: Record<string, unknown>
}

type Translator = ReturnType<typeof useLanguage>["t"]
const CHAIN_SEPARATOR = "\n" + "─".repeat(40) + "\n"
type ErrorPageAction =
  | "checkUpdate"
  | "installUpdate"
  | "openDiagnosticsViewer"
  | "openDiagnosticsFolder"
  | "copyRecentDiagnostics"
  | "sendSupportReport"

const errorPageActionFailureCopy = {
  checkUpdate: "Could not check for updates. Try again, or restart OpenAnalyst.",
  installUpdate: "Could not install the update. Restart OpenAnalyst and check for updates again.",
  openDiagnosticsViewer: "Could not open diagnostics. Use Copy recent diagnostics instead.",
  openDiagnosticsFolder: "Could not open the diagnostics folder. Use Copy recent diagnostics instead.",
  copyRecentDiagnostics: "Could not copy diagnostics. Open the logs folder and copy the recent entries.",
  sendSupportReport: "Could not send the report. Try again, or copy recent diagnostics and share them with support.",
} satisfies Record<ErrorPageAction, string>

function logActionFailure(action: ErrorPageAction, error: unknown) {
  console.error(`[error-page] ${action} failed`, error)
}

function isIssue(value: unknown): value is { message: string; path: string[] } {
  if (!value || typeof value !== "object") return false
  if (!("message" in value) || !("path" in value)) return false
  const message = (value as { message: unknown }).message
  const path = (value as { path: unknown }).path
  if (typeof message !== "string") return false
  if (!Array.isArray(path)) return false
  return path.every((part) => typeof part === "string")
}

function isInitError(error: unknown): error is InitError {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    "data" in error &&
    typeof (error as InitError).data === "object"
  )
}

function safeJson(value: unknown, circular: string): string {
  const seen = new WeakSet<object>()
  const json = JSON.stringify(
    value,
    (_key, val) => {
      if (typeof val === "bigint") return val.toString()
      if (typeof val === "object" && val) {
        if (seen.has(val)) return circular
        seen.add(val)
      }
      return val
    },
    2,
  )
  return json ?? String(value)
}

function formatInitError(error: InitError, t: Translator): string {
  const data = error.data
  const json = (value: unknown) => safeJson(value, t("error.page.circular"))
  switch (error.name) {
    case "MCPFailed": {
      const name = typeof data.name === "string" ? data.name : ""
      return t("error.chain.mcpFailed", { name })
    }
    case "ProviderAuthError": {
      const providerID = typeof data.providerID === "string" ? data.providerID : t("common.unknown")
      const message = typeof data.message === "string" ? data.message : json(data.message)
      return t("error.chain.providerAuthFailed", { provider: providerID, message })
    }
    case "APIError": {
      const message = typeof data.message === "string" ? data.message : t("error.chain.apiError")
      const lines: string[] = [message]

      if (typeof data.statusCode === "number") {
        lines.push(t("error.chain.status", { status: data.statusCode }))
      }

      if (typeof data.isRetryable === "boolean") {
        lines.push(t("error.chain.retryable", { retryable: data.isRetryable }))
      }

      if (typeof data.responseBody === "string" && data.responseBody) {
        lines.push(t("error.chain.responseBody", { body: data.responseBody }))
      }

      return lines.join("\n")
    }
    case "ProviderModelNotFoundError": {
      const { providerID, modelID, suggestions } = data as {
        providerID: string
        modelID: string
        suggestions?: string[]
      }

      const suggestionsLine =
        Array.isArray(suggestions) && suggestions.length
          ? [t("error.chain.didYouMean", { suggestions: suggestions.join(", ") })]
          : []

      return [
        t("error.chain.modelNotFound", { provider: providerID, model: modelID }),
        ...suggestionsLine,
        t("error.chain.checkConfig"),
      ].join("\n")
    }
    case "ProviderInitError": {
      const providerID = typeof data.providerID === "string" ? data.providerID : t("common.unknown")
      return t("error.chain.providerInitFailed", { provider: providerID })
    }
    case "ConfigJsonError": {
      const path = typeof data.path === "string" ? data.path : json(data.path)
      const message = typeof data.message === "string" ? data.message : ""
      if (message) return t("error.chain.configJsonInvalidWithMessage", { path, message })
      return t("error.chain.configJsonInvalid", { path })
    }
    case "ConfigDirectoryTypoError": {
      const path = typeof data.path === "string" ? data.path : json(data.path)
      const dir = typeof data.dir === "string" ? data.dir : json(data.dir)
      const suggestion = typeof data.suggestion === "string" ? data.suggestion : json(data.suggestion)
      return t("error.chain.configDirectoryTypo", { dir, path, suggestion })
    }
    case "ConfigFrontmatterError": {
      const path = typeof data.path === "string" ? data.path : json(data.path)
      const message = typeof data.message === "string" ? data.message : json(data.message)
      return t("error.chain.configFrontmatterError", { path, message })
    }
    case "ConfigInvalidError": {
      const issues = Array.isArray(data.issues)
        ? data.issues.filter(isIssue).map((issue) => "↳ " + issue.message + " " + issue.path.join("."))
        : []
      const message = typeof data.message === "string" ? data.message : ""
      const path = typeof data.path === "string" ? data.path : json(data.path)

      const line = message
        ? t("error.chain.configInvalidWithMessage", { path, message })
        : t("error.chain.configInvalid", { path })

      return [line, ...issues].join("\n")
    }
    case "UnknownError":
      return typeof data.message === "string" ? data.message : json(data)
    default:
      if (typeof data.message === "string") return data.message
      return json(data)
  }
}

function formatErrorChain(error: unknown, t: Translator, depth = 0, parentMessage?: string): string {
  const json = (value: unknown) => safeJson(value, t("error.page.circular"))
  if (!error) return t("error.chain.unknown")

  if (isInitError(error)) {
    const message = formatInitError(error, t)
    if (depth > 0 && parentMessage === message) return ""
    const indent = depth > 0 ? `\n${CHAIN_SEPARATOR}${t("error.chain.causedBy")}\n` : ""
    return indent + `${error.name}\n${message}`
  }

  if (error instanceof Error) {
    const isDuplicate = depth > 0 && parentMessage === error.message
    const parts: string[] = []
    const indent = depth > 0 ? `\n${CHAIN_SEPARATOR}${t("error.chain.causedBy")}\n` : ""

    const header = `${error.name}${error.message ? `: ${error.message}` : ""}`
    const stack = error.stack?.trim()

    if (stack) {
      const startsWithHeader = stack.startsWith(header)

      if (isDuplicate && startsWithHeader) {
        const trace = stack.split("\n").slice(1).join("\n").trim()
        if (trace) {
          parts.push(indent + trace)
        }
      }

      if (isDuplicate && !startsWithHeader) {
        parts.push(indent + stack)
      }

      if (!isDuplicate && startsWithHeader) {
        parts.push(indent + stack)
      }

      if (!isDuplicate && !startsWithHeader) {
        parts.push(indent + `${header}\n${stack}`)
      }
    }

    if (!stack && !isDuplicate) {
      parts.push(indent + header)
    }

    if (error.cause) {
      const causeResult = formatErrorChain(error.cause, t, depth + 1, error.message)
      if (causeResult) {
        parts.push(causeResult)
      }
    }

    return parts.join("\n\n")
  }

  if (typeof error === "string") {
    if (depth > 0 && parentMessage === error) return ""
    const indent = depth > 0 ? `\n${CHAIN_SEPARATOR}${t("error.chain.causedBy")}\n` : ""
    return indent + error
  }

  const indent = depth > 0 ? `\n${CHAIN_SEPARATOR}${t("error.chain.causedBy")}\n` : ""
  return indent + json(error)
}

function formatError(error: unknown, t: Translator): string {
  return formatErrorChain(error, t, 0)
}

function formatDiagnosticEntry(entry: DiagnosticLogEntry): string {
  const fields = entry.fields.length ? ` ${entry.fields.map((field) => `${field.key}=${field.value}`).join(" ")}` : ""
  return `[${entry.timestamp}] ${entry.level} ${entry.source}/${entry.target} ${entry.tags.join(",")} ${entry.message}${fields}`
}

interface ErrorPageProps {
  error: unknown
}

function isDesktopAuthBootstrapError(error: unknown) {
  if (isMissingOpenAnalystSessionError(error)) return true
  const formatted = formatErrorChain(error, () => "", 0)
  return formatted.includes("Missing or invalid Authorization header") || formatted.includes("Invalid or expired token")
}

export function isProviderContextMismatchError(error: unknown): boolean {
  if (typeof error === "string") return /context must be used within a context provider/i.test(error)
  if (!(error instanceof Error)) return false
  if (/context must be used within a context provider/i.test(error.message)) return true
  return isProviderContextMismatchError(error.cause)
}

export function shouldAutoRecoverProviderContextError(input: {
  error: unknown
  lastRecoveryAt: number
  now: number
  cooldownMs?: number
}) {
  if (!isProviderContextMismatchError(input.error)) return false
  return input.now - input.lastRecoveryAt > (input.cooldownMs ?? 15_000)
}

export const ErrorPage: Component<ErrorPageProps> = (props) => {
  const platform = usePlatform()
  const language = useLanguage()
  const appSurfaceLabel = () => (platform.platform === "desktop" ? "OpenAnalyst Desktop" : "OpenAnalyst")
  const needsSignIn = () => platform.platform === "desktop" && isDesktopAuthBootstrapError(props.error)
  /**
   * The network dropped — the app is fine.
   *
   * `fetch` reports a dead network as a TypeError, so this page used to answer a blinking wifi
   * with "Something went wrong", `TypeError: Failed to fetch`, a stack trace, and a Restart
   * button. That is four ways of saying "the app is broken" about the one problem restarting
   * cannot fix. Sign-in is checked first: an expired token is the more specific diagnosis, and
   * it fails while the network is perfectly healthy.
   */
  const offline = () => !needsSignIn() && isNetworkError(props.error)
  const [reportOpen, setReportOpen] = createSignal(false)
  const [reportEmail, setReportEmail] = createSignal("")
  const [reportMessage, setReportMessage] = createSignal("")
  const [reportSending, setReportSending] = createSignal(false)
  const [store, setStore] = createStore({
    checking: false,
    version: undefined as string | undefined,
    actionError: undefined as string | undefined,
    actionMessage: undefined as string | undefined,
  })

  async function checkForUpdates() {
    if (!platform.checkUpdate) return
    setStore("checking", true)
    await platform
      .checkUpdate()
      .then((result) => {
        setStore("actionError", undefined)
        if (result.updateAvailable && result.version) setStore("version", result.version)
        setStore("actionMessage", undefined)
      })
      .catch((err) => {
        showActionFailure("checkUpdate", err)
      })
      .finally(() => {
        setStore("checking", false)
      })
  }

  async function installUpdate() {
    if (!platform.updateAndRestart) return
    await platform
      .updateAndRestart()
      .then(() => setStore("actionError", undefined))
      .catch((err) => {
        showActionFailure("installUpdate", err)
      })
  }

  async function openDiagnosticsViewer() {
    if (!platform.diagnosticsOpenViewer) return
    await platform
      .diagnosticsOpenViewer()
      .then(() => {
        setStore("actionError", undefined)
        setStore("actionMessage", language.t("error.page.diagnostics.viewerOpened"))
      })
      .catch((err) => {
        showActionFailure("openDiagnosticsViewer", err)
      })
  }

  async function openDiagnosticsFolder() {
    if (!platform.diagnosticsOpenDirectory) return
    await platform
      .diagnosticsOpenDirectory()
      .then(() => {
        setStore("actionError", undefined)
        setStore("actionMessage", language.t("error.page.diagnostics.folderOpened"))
      })
      .catch((err) => {
        showActionFailure("openDiagnosticsFolder", err)
      })
  }

  async function copyRecentDiagnostics() {
    if (!platform.diagnosticsTail) return
    await platform
      .diagnosticsTail({ limit: 500 })
      .then((result) => navigator.clipboard?.writeText(result.entries.map(formatDiagnosticEntry).join("\n")))
      .then(() => {
        setStore("actionError", undefined)
        setStore("actionMessage", language.t("error.page.diagnostics.copied"))
      })
      .catch((err) => {
        showActionFailure("copyRecentDiagnostics", err)
      })
  }

  async function collectReportDiagnostics() {
    if (!platform.diagnosticsTail) return { entries: [], unavailable: true }
    return platform
      .diagnosticsTail({ limit: 500 })
      .then((result) => ({
        entries: result.entries.map(formatDiagnosticEntryForReport),
        unavailable: false,
      }))
      .catch(() => ({ entries: [], unavailable: true }))
  }

  async function sendSupportReport() {
    if (reportSending()) return
    setReportSending(true)
    setStore("actionError", undefined)
    await collectReportDiagnostics()
      .then((diagnostics) =>
        submitErrorReport(
          import.meta.env.VITE_OPENANALYST_ERROR_REPORT_URL ??
            defaultErrorReportUrl(import.meta.env.VITE_OPENANALYST_CHANNEL),
          buildErrorReportPayload({
            errorText: formatError(props.error, language.t),
            version: platform.version,
            channel: import.meta.env.VITE_OPENANALYST_CHANNEL,
            platform: platform.platform,
            os: navigator.platform,
            email: reportEmail(),
            message: reportMessage(),
            diagnostics: diagnostics.entries,
            diagnosticsUnavailable: diagnostics.unavailable,
          }),
        ),
      )
      .then((result) => {
        setStore("actionMessage", language.t("error.page.report.sent", { reportId: result.reportId }))
        setReportOpen(false)
      })
      .catch((err) => {
        showActionFailure("sendSupportReport", err)
      })
      .finally(() => {
        setReportSending(false)
      })
  }

  function showActionFailure(action: ErrorPageAction, err: unknown) {
    logActionFailure(action, err)
    setStore("actionMessage", undefined)
    setStore("actionError", errorPageActionFailureCopy[action])
  }

  function signInAgain() {
    window.location.assign("/login")
  }

  const [retrying, setRetrying] = createSignal(false)
  const [recoveringContext, setRecoveringContext] = createSignal(isProviderContextMismatchError(props.error))
  /**
   * Reload and let the app boot again — if the network is back, it simply works.
   *
   * The flag is not decoration: a reload takes long enough that a user with a still-dead
   * connection will press the button repeatedly, and an unchanged page is the app saying
   * nothing at all. It never resets, because a successful reload replaces this page anyway
   * and a failed one lands back here freshly mounted.
   */
  function retry() {
    setRetrying(true)
    window.location.reload()
  }

  onMount(() => {
    if (!isProviderContextMismatchError(props.error)) return
    const key = "openanalyst.context-recovery-at"
    const now = Date.now()
    const lastRecoveryAt = Number(window.sessionStorage.getItem(key) ?? "0")
    if (!shouldAutoRecoverProviderContextError({ error: props.error, lastRecoveryAt, now })) {
      setRecoveringContext(false)
      return
    }
    window.sessionStorage.setItem(key, String(now))
    setRetrying(true)
    const timer = window.setTimeout(() => window.location.reload(), 250)
    onCleanup(() => window.clearTimeout(timer))
  })

  return (
    <div
      data-system-surface="error"
      class="oa-system-surface oa-error-page relative flex-1 h-screen w-screen min-h-0 flex flex-col items-center justify-center font-sans"
    >
      <div class="oa-error-card flex flex-col items-center justify-center gap-6">
        <div class="oa-error-brand">
          <Splash class="oa-error-brand-mark" />
          <div class="min-w-0">
            <div class="oa-system-kicker">{appSurfaceLabel()}</div>
            <div class="oa-error-brand-name">OpenAnalyst</div>
          </div>
        </div>
        <div class="flex flex-col items-center gap-2 text-center">
          <h1 class="oa-system-title">
            {needsSignIn()
              ? "Sign in required"
              : offline()
                ? "Can’t reach OpenAnalyst"
                : recoveringContext()
                  ? "Refreshing OpenAnalyst"
                  : language.t("error.page.title")}
          </h1>
          <p class="oa-system-description">
            {needsSignIn()
              ? `Your local session is missing or expired. Sign in again to reconnect ${appSurfaceLabel()}.`
              : offline()
                ? `${appSurfaceLabel()} couldn’t reach the network. Your work is safe — check your connection and try again.`
                : recoveringContext()
                  ? "A live development update replaced shared app context. Your project files are safe; the app is reloading the interface."
                  : language.t("error.page.description")}
          </p>
        </div>
        {/* A stack trace is what you show someone whose app is broken. It is noise to someone
            whose wifi dropped, and it is what made a blip read as a crash. */}
        <Show when={!needsSignIn() && !offline() && !recoveringContext()}>
          <TextField
            value={formatError(props.error, language.t)}
            readOnly
            copyable
            multiline
            class="oa-error-details max-h-96 w-full font-mono text-xs no-scrollbar"
            label={language.t("error.page.details.label")}
            hideLabel
          />
        </Show>
        <div class="oa-system-actions flex items-center gap-3">
          <Show
            when={needsSignIn()}
            fallback={
              <Show
                when={offline()}
                fallback={
                  <Show
                    when={recoveringContext()}
                    fallback={
                      <Button size="large" variant="primary" onClick={platform.restart}>
                        {language.t("error.page.action.restart")}
                      </Button>
                    }
                  >
                    <Button size="large" variant="primary" onClick={retry} disabled={retrying()}>
                      {retrying() ? "Refreshing…" : "Reload app"}
                    </Button>
                  </Show>
                }
              >
                {/* Retry, not Restart: the app never broke, so tearing it down and building it
                    back up is a long way round to the reload this actually needs. */}
                <Button size="large" variant="primary" onClick={retry} disabled={retrying()}>
                  {retrying() ? "Reconnecting…" : "Try again"}
                </Button>
              </Show>
            }
          >
            <Button size="large" variant="primary" onClick={signInAgain}>
              Sign in again
            </Button>
          </Show>
          {/* Reporting a dropped connection as a bug wastes the user's time and ours. */}
          <Show when={!needsSignIn() && !offline() && !recoveringContext()}>
            <Button size="large" variant="ghost" onClick={() => setReportOpen(true)}>
              {language.t("error.page.action.report")}
            </Button>
          </Show>
          <Show when={platform.checkUpdate && !recoveringContext()}>
            <Show
              when={store.version}
              keyed
              fallback={
                <Button size="large" variant="ghost" onClick={checkForUpdates} disabled={store.checking}>
                  {store.checking
                    ? language.t("error.page.action.checking")
                    : language.t("error.page.action.checkUpdates")}
                </Button>
              }
            >
              {(version) => (
                <Button size="large" variant="primary" onClick={installUpdate}>
                  {language.t("error.page.action.updateTo", { version })}
                </Button>
              )}
            </Show>
          </Show>
          <Show when={platform.quit}>
            <Button size="large" variant="ghost" onClick={() => void platform.quit?.()}>
              Quit OpenAnalyst
            </Button>
          </Show>
        </div>
        <Show when={reportOpen() && !needsSignIn()}>
          <form
            class="oa-system-actions flex w-full max-w-xl flex-col items-stretch gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              void sendSupportReport()
            }}
          >
            <p class="oa-system-row-note text-center">{language.t("error.page.report.diagnosticsNote")}</p>
            <input
              aria-label={language.t("error.page.report.email.label")}
              placeholder={language.t("error.page.report.email.placeholder")}
              value={reportEmail()}
              onInput={(event) => setReportEmail(event.currentTarget.value)}
              class="oa-error-report-input"
            />
            <textarea
              aria-label={language.t("error.page.report.message.label")}
              placeholder={language.t("error.page.report.message.placeholder")}
              value={reportMessage()}
              onInput={(event) => setReportMessage(event.currentTarget.value)}
              class="oa-error-details min-h-24 w-full font-sans text-sm"
            />
            <div class="oa-system-actions flex items-center justify-center gap-2">
              <Button
                type="button"
                size="large"
                variant="secondary"
                disabled={reportSending()}
                onClick={sendSupportReport}
              >
                {language.t(reportSending() ? "error.page.report.sending" : "error.page.report.submit")}
              </Button>
              <Button type="button" size="large" variant="ghost" onClick={() => setReportOpen(false)}>
                {language.t("error.page.report.cancel")}
              </Button>
            </div>
          </form>
        </Show>
        <Show when={platform.diagnosticsOpenViewer || platform.diagnosticsOpenDirectory || platform.diagnosticsTail}>
          <div class="oa-system-actions flex flex-wrap items-center justify-center gap-2">
            <Show when={platform.diagnosticsOpenViewer}>
              <Button size="large" variant="secondary" onClick={openDiagnosticsViewer}>
                {language.t("error.page.diagnostics.openViewer")}
              </Button>
            </Show>
            <Show when={platform.diagnosticsOpenDirectory}>
              <Button size="large" variant="ghost" onClick={openDiagnosticsFolder}>
                {language.t("error.page.diagnostics.openFolder")}
              </Button>
            </Show>
            <Show when={platform.diagnosticsTail}>
              <Button size="large" variant="ghost" onClick={copyRecentDiagnostics}>
                {language.t("error.page.diagnostics.copyRecent")}
              </Button>
            </Show>
          </div>
        </Show>
        <Show when={store.actionError} keyed>
          {(message) => <p class="oa-system-error-message text-center max-w-2xl">{message}</p>}
        </Show>
        <Show when={store.actionMessage} keyed>
          {(message) => <p class="oa-system-row-note text-center max-w-2xl">{message}</p>}
        </Show>
        <div class="flex flex-col items-center gap-2">
          <Show when={platform.version} keyed>
            {(version) => <p class="oa-system-mono-muted">{language.t("error.page.version", { version })}</p>}
          </Show>
        </div>
      </div>
    </div>
  )
}
